import { useCallback, useMemo, useState } from "react";
import { Tabs } from "antd";
import { useScopeParams } from "@/hooks/useScopeParams";
import SimpleTableTemplate from "@/components/Templates/SimpleTableTemplate";
import PageHeader from "@/components/UI/PageHeader";

import trialIssues from "@/assets/data/json_output/trial_issues.json";
import trialRules from "@/assets/data/json_output/trial_rules.json";
import { useSelectableTableView } from "@/utils/tableHelpers";
import {
  createIssuesColumns,
  filterHiddenColumns,
  type TableRow,
} from "./issuesTableConfig";
import { createRulesColumns } from "./rulesTableConfig";
import TrialIssuesDrawer from "./TrialIssuesDrawer";
import TrialIssuesAnalytics from "./TrialIssuesAnalytics/TrialIssuesAnalytics";

const trialIssuesRows = trialIssues as TableRow[];
const trialRulesRows = trialRules as TableRow[];

export default function TrialIssues() {
  const { buildUrl } = useScopeParams();
  const [issuesData, setIssuesData] = useState<TableRow[]>(
    () => trialIssuesRows,
  );
  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<TableRow>();

  const assigneeOptions = useMemo(() => {
    const options = new Set<string>();

    [...issuesData, ...trialRulesRows].forEach((row) => {
      const discrepancyAssignee = row.discrepancy_assignee;
      const assignee = row.assignee;

      if (
        typeof discrepancyAssignee === "string" &&
        discrepancyAssignee.trim().length > 0
      ) {
        options.add(discrepancyAssignee);
      }

      if (typeof assignee === "string" && assignee.trim().length > 0) {
        options.add(assignee);
      }
    });

    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [issuesData]);

  const handleIssueAssigneeChange = useCallback(
    (issueId: string, nextAssignee: string | null) => {
      setIssuesData((currentRows) =>
        currentRows.map((row) =>
          row.discrepancy_id === issueId
            ? { ...row, discrepancy_assignee: nextAssignee || "" }
            : row,
        ),
      );
      setSelectedRecord((currentRecord) =>
        currentRecord?.discrepancy_id === issueId
          ? { ...currentRecord, discrepancy_assignee: nextAssignee || "" }
          : currentRecord,
      );
    },
    [setSelectedRecord],
  );

  const issuesColumns = useMemo(
    () =>
      createIssuesColumns(
        issuesData,
        buildUrl,
        assigneeOptions,
        handleIssueAssigneeChange,
      ),
    [assigneeOptions, buildUrl, handleIssueAssigneeChange, issuesData],
  );
  const rulesColumns = useMemo(() => createRulesColumns(trialRulesRows), []);

  const items = [
    {
      key: "issues",
      label: "Issues",
      children: (
        <SimpleTableTemplate
          fileName="trial_issues"
          data={issuesData}
          downloadData={filterHiddenColumns(issuesData)}
          columns={issuesColumns}
          rowKey="discrepancy_id"
          onRow={getClickableRowProps}
        />
      ),
    },
    {
      key: "rules",
      label: "Rules",
      children: (
        <SimpleTableTemplate
          fileName="trial_rules"
          data={trialRulesRows}
          columns={rulesColumns}
          rowKey="discrepancy_rule_id"
        />
      ),
    },
    {
      key: "analytics",
      label: "Analytics",
      children: (
        <TrialIssuesAnalytics
          issuesData={issuesData}
          rulesData={trialRulesRows}
        />
      ),
    },
  ];

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 p-4">
      <PageHeader
        title="Data: Trial Issues"
        tooltipText="Review trial-level data issues and the rules that generated them, including priority, resolution status, source system, and assignee."
      />
      <Tabs className="w-full" items={items} />
      <TrialIssuesDrawer
        selectedRecord={selectedRecord}
        setSelectedRecord={setSelectedRecord}
        assigneeOptions={assigneeOptions}
        onAssigneeChange={handleIssueAssigneeChange}
      />
    </div>
  );
}
